import React, { useEffect } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { NetworkStatus, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Button, ButtonType } from '@src/components/Button'
import { LoadingSpinner } from '@src/components/Loaders/LoadingSpinner'
import { MerchantType } from '../../../graphql-types'
import { OutletReviewsDocument } from './queries/__generated__/outletReviews.graphql-interface'

import {
  ReviewsSheetContainer,
  ReviewOverviewContainer,
  ReviewOverviewNumberContainer,
  RatingBoldText,
  RatingSmallText,
  ReviewOverviewStarContainer,
  StarRatingRow,
  RatingHeader,
  SheetRatingStar,
  ReviewsEmptyState,
  ReviewList,
  ReviewCard,
  ReviewMeta,
  ReviewRating,
  ReviewTimestamp,
  ReviewText,
  ReviewCategories,
  ReviewCustomerName,
  ReviewReply,
  ReviewReplyLabel,
  ReviewReplyText,
  SheetRatingStarHalf,
  AverageCountAndStarsContainer,
  StarsContainer,
} from './OutletReviews.styles'
import {
  Header,
  HeaderContainer,
  Logo,
} from '../OutletDetails/OutletDetailsStyles'
import { imageJitURL } from '@src/utils/imageJitURL'
import { useOutletPageContext } from '../OutletPageContext'
import { startCase } from 'lodash'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { BooleanParam, useQueryParam } from 'use-query-params'

const PAGE_SIZE = 10

type ReviewRatingKey = 'averageQuality' | 'averageService' | 'averageDelivery'

const formatReviewTimestamp = (value?: string | null): string => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return formatDistanceToNow(date, {
    addSuffix: true,
  })
}

type ReviewsBottomSheetProps = {
  outletId: string
  averageRating: string | null
  reviewCount: number
}

export const ReviewsBottomSheet: React.FC<ReviewsBottomSheetProps> = ({
  outletId,
  averageRating,
  reviewCount,
}) => {
  const { t } = useTranslation([
    'outletOverview',
    'singleOrder',
    'outletDetails',
  ])

  const {
    outletFulfilment: { outlet },
  } = useOutletPageContext()

  const marketplace = useMarketplace()
  const [, setShowReviews] = useQueryParam('showReviews', BooleanParam)

  useEffect(() => {
    if (!marketplace.allowReviews) {
      setShowReviews(undefined)
    }
  }, [])

  const { restaurant, displayName, outletLogoOverride } = outlet
  const { data, loading, error, fetchMore, networkStatus } = useQuery(
    OutletReviewsDocument,
    {
      variables: {
        outletId,
        input: { pageSize: PAGE_SIZE },
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: !outletId,
    }
  )

  const connection = data?.outletReviews

  const reviews = connection?.edges.map(edge => edge?.node) ?? []

  const effectiveCount = connection?.totalCount ?? reviewCount

  const displayAverage =
    averageRating && averageRating.trim().length > 0 ? averageRating : null

  const hasNextPage = connection?.pageInfo.hasNextPage ?? false
  const endCursor = connection?.pageInfo.endCursor ?? null
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore

  const handleLoadMore = async () => {
    if (!hasNextPage || !endCursor) return

    await fetchMore({
      variables: {
        outletId,
        input: { pageSize: PAGE_SIZE, startKey: endCursor },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.outletReviews || !previousResult?.outletReviews) {
          return previousResult ?? fetchMoreResult
        }

        return {
          outletReviews: {
            ...fetchMoreResult.outletReviews,
            edges: [
              ...(previousResult.outletReviews.edges ?? []),
              ...(fetchMoreResult.outletReviews.edges ?? []),
            ],
          },
        }
      },
    })
  }

  const ratingHeaders: { key: ReviewRatingKey; translation: string }[] = [
    {
      key: 'averageQuality',
      translation:
        marketplace.merchantType === MerchantType.RETAIL
          ? 'quality_retail'
          : 'quality_takeaway',
    },
    { key: 'averageService', translation: 'service' },
    { key: 'averageDelivery', translation: 'delivery_time' },
  ]

  return (
    <ReviewsSheetContainer data-test-id="outlet-overview-reviews-sheet">
      <HeaderContainer>
        <Header>{displayName}</Header>
        <Logo
          role="img"
          aria-label={`${displayName} logo`}
          imageUrl={imageJitURL(outletLogoOverride || restaurant.image, {
            resize: {
              width: 64,
              height: 64,
              fit: 'cover',
            },
          })}
        />
      </HeaderContainer>
      <ReviewOverviewContainer>
        <ReviewOverviewNumberContainer>
          <RatingBoldText>{displayAverage}</RatingBoldText>
          <RatingSmallText>
            {effectiveCount > 1
              ? t('reviews_sheet_review_count_plural', {
                  count: effectiveCount,
                })
              : t('reviews_sheet_review_count', {
                  count: effectiveCount,
                })}
          </RatingSmallText>
        </ReviewOverviewNumberContainer>
        <ReviewOverviewStarContainer>
          {ratingHeaders.map((header, index) => {
            const individualScoreAverage = outlet.reviewSummary
              ? outlet.reviewSummary[header.key]
              : null

            return (
              <StarRatingRow key={`${header}-${index}`}>
                <RatingHeader>{t(`${header.translation}`)}</RatingHeader>
                <AverageCountAndStarsContainer>
                  <StarsContainer>
                    {Array.from({ length: 5 }).map((_, index) => {
                      const rating = Number(individualScoreAverage)
                      const starNumber = index + 1
                      if (rating >= starNumber) {
                        return <SheetRatingStar key={index} />
                      }
                      if (rating >= starNumber - 0.5) {
                        return <SheetRatingStarHalf key={index} />
                      }
                      return <SheetRatingStar key={index} fillColor="#fff" />
                    })}
                  </StarsContainer>
                  <RatingHeader>{individualScoreAverage}</RatingHeader>
                </AverageCountAndStarsContainer>
              </StarRatingRow>
            )
          })}
        </ReviewOverviewStarContainer>
      </ReviewOverviewContainer>

      {loading && !connection ? (
        <LoadingSpinner text={t('reviews_sheet_loading')} />
      ) : error ? (
        <ReviewsEmptyState>{t('reviews_sheet_error')}</ReviewsEmptyState>
      ) : !reviews.length ? (
        <ReviewsEmptyState>{t('reviews_sheet_empty_state')}</ReviewsEmptyState>
      ) : (
        <>
          <ReviewList>
            {reviews.map(review => {
              const averageScore = review.averageRating
              const averageDisplay =
                averageScore !== null
                  ? averageScore.toFixed(1)
                  : t('reviews_sheet_not_rated')
              const timestamp = formatReviewTimestamp(review.createdAt)
              const replyTimestamp = formatReviewTimestamp(review.replyDate)

              const categories = [
                {
                  label: t(
                    marketplace.merchantType === MerchantType.RETAIL
                      ? 'quality_retail'
                      : 'quality_takeaway'
                  ),
                  value: review.foodQuality,
                },
                {
                  label: t('review_restaurant_service', { ns: 'singleOrder' }),
                  value: review.restaurantService,
                },
                {
                  label: t('review_delivery_time', { ns: 'singleOrder' }),
                  value: review.deliveryTime,
                },
              ]

              return (
                <ReviewCard key={review.id}>
                  <ReviewMeta>
                    <ReviewRating>
                      <SheetRatingStar />
                      {averageDisplay}
                    </ReviewRating>
                    <ReviewTimestamp>{timestamp}</ReviewTimestamp>
                  </ReviewMeta>
                  {review.reviewText && (
                    <ReviewText>{review.reviewText}</ReviewText>
                  )}
                  <ReviewCategories>
                    {categories.map(category => (
                      <span key={category.label}>
                        {category.label}:{' '}
                        {typeof category.value === 'number'
                          ? category.value
                          : t('reviews_sheet_not_rated')}
                      </span>
                    ))}
                    {review.customerName && (
                      <ReviewCustomerName>
                        {startCase(review.customerName)}
                      </ReviewCustomerName>
                    )}
                  </ReviewCategories>
                  {review.reply && (
                    <ReviewReply>
                      <ReviewReplyLabel>
                        {replyTimestamp
                          ? t('reviews_sheet_reply_label_with_time', {
                              time: replyTimestamp,
                            })
                          : t('reviews_sheet_reply_label')}
                      </ReviewReplyLabel>
                      <ReviewReplyText>{review.reply}</ReviewReplyText>
                    </ReviewReply>
                  )}
                </ReviewCard>
              )
            })}
          </ReviewList>
          {hasNextPage && (
            <Button
              buttonType={ButtonType.SECONDARY}
              content={t('reviews_sheet_load_more')}
              onClick={handleLoadMore}
              loading={isFetchingMore}
              dataTestId="outlet-overview-reviews-load-more"
              width="100%"
            />
          )}
        </>
      )}
    </ReviewsSheetContainer>
  )
}
